import api from "../api";

export interface Vendor {
  vendorId: number;
  firmInfo: string;
  contactPerson: string;
  contactNumber: string;
  email: string;
  address: string;
}

export interface Customer {
  userId: number;
  name: string;
  primaryContactNbr: string;
  email: string | null;
  address: string;
}

export interface InvoiceDetail {
  invoiceNo: string;
  oilseedWeight: number;
  vendor: Vendor;
  rawMaterialName: string;
}

export interface OilIntakeLog {
  oilIntakeLogId: number;
  oilIntakeLogNo: string;
  submittedDate: string;
  startTime: string;
  endTime: string;
  oilSeedName: string;
  oilSeedWeight: number;
  moistureContent: number;
  gaanaDetails: string;
  operator1Name: string;
  operator2Name: string | null;
  bull1Details: string;
  bull2Details: string | null;
  invoiceDetails: InvoiceDetail[];
  posUnit: string;
}

export interface RawMaterialPurchaseData {
  purchaseId: number;
  invoiceNumber: string;
  purchaseDate: string;
  vendor: Vendor;
  rawMaterialName: string;
  categoryName: string;
  invoicedQuantity: number;
  acceptedQuantity: number;
  totalAmount: number;
  comments: string;
  posCode: string;
  posUnit: string;
}

export interface BatchData {
  batchId: number;
  batchNo: string;
  manufactureDate: string;
  expiryDate: string;
  stock: number;
  availableStock: number;
  productName: string;
  subCategoryName: string;
  posCode: string;
  oilIntakeLogs: OilIntakeLog[];
  hasReport: boolean;
  reportId?: number;
  reportDate?: string;
  posUnit: string;
}

export interface BatchDetail {
  batchId: number;
  quantity: number;
}

export interface OrderProduct {
  productName: string;
  quantity: number;
  unitPrice: number;
  grossAmount: number;
  batchIds: string;
  batches: BatchDetail[];
}

export interface InvoiceProduct {
  productName: string;
  invoicedQuantity: number;
  unitPrice: number;
  amount: number;
}

export interface OrderInvoice {
  orderInvoiceId: number;
  invoiceNo: number;
  invoiceDate: string;
  taxableAmount: number;
  taxAmount: number;
  grossAmount: number;
  paymentMode: string;
  posCode: string;
  products: InvoiceProduct[];
}

export interface OrderData {
  orderId: number;
  orderDate: string;
  grossAmount: number;
  orderStatus: string;
  customer: Customer;
  products: OrderProduct[];
  posCode: string;
  comment: string;
  invoices: OrderInvoice[];
}

export type TimelineStage = 'RawMaterialPurchase' | 'Batch' | 'Order';

export interface TimelineItem {
  date: string;
  stage: TimelineStage;
  title: string;
  description: string;
  data: RawMaterialPurchaseData | BatchData | OrderData;
}

export interface TraceabilityResponse {
  timeline: TimelineItem[];
  message: string;
  isSuccess: boolean;
}

export type TraceType = 'BatchId' | 'RawMaterialInvoiceNumber' | 'OrderId' | 'InvoiceNo';

const trace = async (type: TraceType, value: string): Promise<TraceabilityResponse> => {
  try {
    const response = await api.get(`/api/Traceability/trace?type=${type}&value=${encodeURIComponent(value)}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching traceability data:', error);
    throw error;
  }
};

const traceabilityService = {
  trace,
};

export default traceabilityService;
